import { EPublisherSolutionModelSubType, Portal } from 'common/contracts';
import { IPermissions } from 'common/permissions.types';
import { ESideBarContext } from 'design-system/AcSidebar/types';
import { UserDetails } from 'pages/Login/Login.types';
import { ESharedRoutes } from 'routes/types';
import { permissionsUtil } from 'utils/permissionsUtil';

export const DEFAULT_PORTAL_LEVEL_STORE_PAGE = ESharedRoutes.BUNDLES;

export const getLandingPageFromUrl = (
  itemUrl: string | undefined,
  getPermissions: () => IPermissions,
  userDetails: UserDetails | null | undefined
): string | null => {
  if (!itemUrl) return null;
  
  const isPortalItem = itemUrl.startsWith('portal/');
  const isProjectItem = itemUrl.startsWith('project/');
  const isStoreItem = itemUrl.startsWith('store/');
  
  if (!isPortalItem && !isProjectItem && !isStoreItem) return null;

  const urlParts = itemUrl.split('/');
  
  if (isPortalItem && urlParts.length > 2) {
    return urlParts[urlParts.length - 1];
  }
  
  if (isStoreItem) {
    return DEFAULT_PORTAL_LEVEL_STORE_PAGE;
  }
  
  if (isProjectItem) {
    const projectId = urlParts[1];
    const permissions = getPermissions();
    return checkDefaultProjectPage(permissions, projectId, userDetails);
  }
  
  return null;
};

export const getPublisherIdForPortal = (
  portalId: string | undefined,
  portals: Portal[] | undefined
): string | undefined => {
  if (!portalId || !portals) return undefined;
  
  const portal = portals.find((p) => p.portalId === portalId);
  return portal?.stores?.[0]?.storeId;
};

export const getPortalIdForStore = (
  storeId: string | undefined,
  portals: Portal[] | undefined
): string | undefined => {
  if (!storeId || !portals) return undefined;
  
  const portal = portals.find((p) =>
    p.stores?.some((store) => store.storeId === storeId)
  );
  return portal?.portalId;
};

export const checkDefaultPageName = (
  context: ESideBarContext,
  permissions?: IPermissions,
  id?: string
): string => {
  if (context === ESideBarContext.PORTAL) {
    return `${ESharedRoutes.SETTINGS}/portal`;
  }

  if (permissions && id) {
    return checkDefaultProjectPage(permissions, id);
  }

  return '';
};

export const checkDefaultProjectPage = (
  permissions: IPermissions,
  publisherId?: string,
  userDetails?: UserDetails | null
) => {
  if (!publisherId) {
    return 'statistics';
  }

  if (!userDetails) {
    return 'statistics';
  }

  const hasAnalyticsViewPermission = permissionsUtil.canAccessAnalytics(
    permissions,
    publisherId
  );

  const isSingleProject = userDetails.projects?.length === 1;
  const isPaymentLinkProject =
    userDetails.projects?.[0]?.projectSubType ===
    EPublisherSolutionModelSubType.PAYMENT_LINKS;

  if (isSingleProject && isPaymentLinkProject && permissionsUtil.canAccessSettings(permissions, publisherId || '')) {
    return 'settings';
  }

  if (hasAnalyticsViewPermission) {
    return 'statistics';
  }

  return 'bundles';
};
